import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {DbActiveModal} from "../../../../../modules/modal/modal-ref";
import {ActivityAction, ActivityStatus} from "../../../../../activities/activity";
import {filter, first, switchMap, takeUntil} from "rxjs/operators";
import {DbResponse} from "../../../../../shared/interceptors/db-response";
import {Product} from "../../../product";
import {Store} from "@ngrx/store";
import * as fromStore from "../../../../../store";
import {Website} from "../../../website";
import {WebsiteService} from "../../../../website.service";
import {Subject} from "rxjs";
import {
  CreateAIProcessStepSlug
} from "../../../../../components/pages/setup/create-ai/create-ai-builder/components/create-ai-process/create-ai-process.component";
import {
  NotificationDialogComponent
} from "../../../../../shared/components/dialogs/notification-dialog/notification-dialog.component";
import {DialogService} from "../../../../../../ng2-bootstrap-modal/dialog.service";
import {TypeUtils} from "../../../../../shared/utils/type-utils";
import {BuilderTemplates} from "../../../builder-templates/builder-templates";
import {DbModalService} from "../../../../../modules/modal/modal";
import {DashboardUtils} from "../../../../../shared/utils/dashboard-utils";
import {GoogleAnalyticsService} from "../../../../../shared/services/google-analytics/google-analytics.service";
import {IAIBuilderPage} from "../../ai-builder-pages/ai-builder-pages-table/ai-builder-pages-table.component";
import {UserSubscription, UserSubscriptionCategory} from "../../../../../user/user-subscription";
import {Config} from "../../../../../config/config";
import {ProductService} from "../../../product.service";
import {Router} from "@angular/router";
import {AiBuilderCommonService} from "../../ai-builder-common.service";

type CreatePageStep = 'options' | 'builder' | 'templates' | 'blank' | 'recreateBuilder';

const ADDING_ACTIVITIES =
  ['Extracting content.', 'Creating layout.', 'Extracting layout styles.',
    'Creating widgets.', 'Preparing template archive.', 'Website template is ready.'];

@Component({
    selector: 'db-ai-builder-create-page',
    templateUrl: './ai-builder-create-page.component.html',
    styleUrls: ['./ai-builder-create-page.component.scss'],
    standalone: false
})
export class AiBuilderCreatePageComponent implements OnInit, OnDestroy {

  @Input() website: Website;
  @Input() userEmail: string;
  @Input() kitData: any;
  @Input() menuItemId?: any;
  @Input() menuItemPosition?: any;
  @Input() menuTermId?: any;
  @Input() builderPlugin: Product | null;
  @Input() elementorPlugin: Product | null;
  @Input() trialUser: boolean;
  @Input() hasFrontPage: boolean;
  @Input() hideBackButton?: boolean;
  @Input() choosedStep?: CreatePageStep;
  @Input() demoUser?: boolean;
  public activeTheme: Product;
  public defaultThemes: boolean;
  public recreateStarted: boolean;
  public websiteTenWebTheme: Product | null;
  public tenWebTheme: Product | null;
  public step: CreatePageStep = 'options';
  public templateScreenshot: string;
  public importTemplateType: string;
  public templateInfo: string;
  public templateCategory: string;
  public recreationStep: CreateAIProcessStepSlug;
  public pages: IAIBuilderPage[] | null = null;
  public addedPage: IAIBuilderPage;
  public websiteElementorUrl: string;
  public showFailedDialog: boolean;
  public userSubscription: UserSubscription;
  public UserSubscriptionCategory: any = UserSubscriptionCategory;

  private _availableUpdatesCount: number = 0;

  private _destroyStream$: Subject<boolean> = new Subject<boolean>();
  private _interrupt$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private _store: Store<fromStore.DbStoreState>,
    private _activeModal: DbActiveModal,
    private _dialogService: DialogService,
    private _websiteService: WebsiteService,
    private _modalService: DbModalService,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _productService: ProductService,
    private _router: Router,
    private aiBuilderService: AiBuilderCommonService,
  ) {
  }

  ngOnInit() {
    if (this.choosedStep) {
      this.step = this.choosedStep;
    }
    this._getTenWebTheme();
    this._initData();
    this._store.select(fromStore.getTenWebProductsLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => this._store.select(fromStore.getTenWebTheme)),
        first()
      ).subscribe(tenWebTheme => {
      this.tenWebTheme = tenWebTheme;
    });
    this._store.select(fromStore.getWebsiteActiveTheme(this.website.id))
      .pipe(
        takeUntil(this._destroyStream$)
      )
      .subscribe((theme: Product) => {
        this.activeTheme = theme;
        this.defaultThemes = this.activeTheme && Config.wpThemesSlugs.includes(this.activeTheme.slug);
      });
  }

  ngOnDestroy() {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
    this._interrupt$.next(true);
    this._interrupt$.complete();
  }

  private _initData(): void {
    this._store.select(fromStore.getCreatedPages(this.website.id))
      .subscribe((pages) => {
        this._getPagesList(pages);
      });
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => this._store.select(fromStore.getUserSubscription)),
        first()
      )
      .subscribe((userSubscription: UserSubscription) => {
        this.userSubscription = userSubscription;
      });
  }

  private _getPagesList(pages): void {
    if (pages && TypeUtils.isDefined(pages) && !TypeUtils.isEmpty(pages)) {
      this.pages = pages.filter((page) => page && page.pageType === 'page');
      const frontPage = pages.find(page => page && !!page.frontPage);
      this.addedPage = frontPage;
    }
  }

  public changeStep(step: String, templateScreenshot?: string, selectedCategory?: string): void {
    if (step === 'builder-v2') {
      if (this.demoUser) {
        this.showUpgradePopup('Ai');
        return;
      }
      this._modalService.dismissAll();
      this._activeModal.close();
      this._router.navigate([`./websites/${this.website.id}/generate-page`], {
        queryParams: { fromDashboard: true }
      });
      return;
    }
    this.step = step as CreatePageStep;
    this.templateScreenshot = TypeUtils.isDefined(templateScreenshot) ? templateScreenshot : null;
    selectedCategory && (this.templateCategory = selectedCategory);
  }

  private _getPluginActivity(data, from): void {
    this._store.select(fromStore.getOngoingActivity)
      .pipe(
        takeUntil(this._destroyStream$ || this._interrupt$),
        filter(activity => activity && activity.websiteId === this.website.id
          && activity.meta && activity.meta.product_data && activity.meta.product_data.type
          && (activity.meta.product_data.type === "plugin" || activity.meta.product_data.type === "theme")
        )
      )
      .subscribe(activity => {
        if (activity.status === ActivityStatus.DONE) {
          --this._availableUpdatesCount;
          !this._availableUpdatesCount && this._interrupt$.next(true);
        }
        if (!this._availableUpdatesCount) {
          if (from === 'recreate') {
            !this.recreateStarted && this._recreateWebsite(data.url, data.pageTitle, data.kitData);
          } else {
            this._importTemplate(data.template);
          }
        }
      });
  }

  private _getOngoingActivity(): void {
    this._store.select(fromStore.getOngoingActivity)
      .pipe(
        takeUntil(this._destroyStream$ || this._interrupt$),
        filter(activity => activity && activity.websiteId === this.website.id)
      )
      .subscribe(activity => {
        if (activity.status === ActivityStatus.FAILED && !this.showFailedDialog) {
          this.showFailedDialog = true;
          this._activeModal.close();
          this._modalService.dismissAll();
          this._showFailedDialog();
        }
        if (ADDING_ACTIVITIES.includes(activity.message) || (activity.action === 'install_hosted_template' && activity.status !== ActivityStatus.DONE)) {
          this.recreationStep = 'adding';
        }
        if (activity.action === 'install_template') {
          this.recreationStep = 'finishing';
          if (activity && activity.meta && activity.meta.page_id && !this.hasFrontPage && activity.status === ActivityStatus.DONE) {
            this._setFrontPage(activity.meta.page_id);
          }
        }
        if (activity.action === 'install_hosted_template') {
          if (activity.status === ActivityStatus.DONE) {
            if (this.importTemplateType === 'create-page-ai') {
            } else if (this.importTemplateType === 'pre-made-page') {
            }
            if (activity && activity.meta && activity.meta.page_id && !this.hasFrontPage) {
              this._setFrontPage(activity.meta.page_id);
            } else {
              this._store.dispatch(new fromStore.LoadBuilderCreatedPages(this.website.id));
            }
            this.recreationStep = 'done';
            this._activeModal.close();
            this._modalService.dismissAll();
          } else {
            this.recreationStep = 'finishing';
          }
        }
      });
  }

  private _showFailedDialog(): void {
    this._dialogService.addDialog(NotificationDialogComponent, {
      header: 'Something went wrong :/',
      message: 'We encountered an error while recreating your website. Contact our <a target=\'_blank\' href=\'https://10web.io/contact-us/\'> customer care </a> team to get it fixed ASAP.',
      buttonText: 'Got It',
      buttonColor: 'blue',
      action: 'failed_import_template',
      hasCloseButton: true
    });
  }

  private _setFrontPage(pageId): void {
    this._websiteService.setFrontPage(this.website.id, pageId)
      .pipe(
        first(response => !!response)
      )
      .subscribe(response => {
        if (response.success) {
          this._productService.updateScreenShot(this.website.id);
          this._store.dispatch(new fromStore.LoadBuilderCreatedPages(this.website.id));
        }
      });
  }


  private showSuccessDialog(action, url?, title?: string): void {
    this._store.dispatch(new fromStore.LoadBuilderCreatedPages(this.website.id));
    this._dialogService.addDialog(NotificationDialogComponent, {
      image: true,
      header: 'Congrats!',
      message: 'Your page was successfully created.',
      buttonText: this.userSubscription.trialClient ? 'Preview' : 'Preview & edit',
      buttonColor: 'blue',
      action: action,
      hasAdminLink: false,
      websiteId: this.website.id,
      adminLink: this.websiteElementorUrl,
      hasCloseButton: true
    })
      .subscribe((confirmed: string): void => {
        if (confirmed) {
          if (!!this.userSubscription.trialClient) {
            const pageUrl: string = this._getPageRedirectUrl(url);
            window.open(pageUrl, "_blank", "noopener");
          } else {
            if (confirmed === 'imported_template' || confirmed === 'install_hosted_template') {
              window.open(DashboardUtils.generateWpAdminRedirectorUrl(this.website.id, this.website.adminElementorUrl, this.userEmail), "_blank", "noopener");
              /*this._router.navigate(['/upgrade-plan']);*/
            } else if (confirmed === 'blank_page' && url) {
              window.open(DashboardUtils.generateWpAdminRedirectorUrl(this.website.id, url, this.userEmail), "_blank", "noopener");
            }
          }
        }
      });
  }

  private _getTenWebTheme(): void {
    this._store.select(fromStore.getWebsiteTenWebTheme(this.website.id))
      .pipe(
        takeUntil(this._destroyStream$),
        filter(websiteTenWebTheme => !!websiteTenWebTheme),
      )
      .subscribe(websiteTenWebTheme => this.websiteTenWebTheme = websiteTenWebTheme);
  }

  public installAndActivate(websiteTenWebTheme): void {
    this._store.dispatch(new fromStore.InitWebsiteProductAction({
      website: this.website,
      products: websiteTenWebTheme ? [websiteTenWebTheme] : [this.tenWebTheme],
      action: !websiteTenWebTheme.installed ? ActivityAction.INSTALL_ACTIVATE : ActivityAction.ACTIVATE,
      network: 1
    }));
  }

  public recreate({title, url}): void {
    this.aiBuilderService.fromAddPage.next(true);
    this._activeModal.hideCloseBtn(true);
    this._activeModal.setBackdrop('static');
    this._activeModal.resize(432, 471);
    this.step = 'recreateBuilder';
    this.recreationStep = 'recreating';
    const data = {
      url,
      pageTitle: title,
      kitData: this.kitData
    };
    this._doRequiredActionsBeforeRun() ? this._getPluginActivity(data, 'recreate') : !this.recreateStarted && this._recreateWebsite(url, title, this.kitData);
  }

  /** return true if plugin update available **/
  private _doRequiredActionsBeforeRun(): boolean {
    const products = [];
    if (this.builderPlugin && this.builderPlugin.updateAvailable) {
      // products.push(this.builderPlugin);
    }
    if (this.elementorPlugin && this.elementorPlugin.updateAvailable) {
      products.push(this.elementorPlugin);
    }
    if (this.websiteTenWebTheme && this.websiteTenWebTheme.updateAvailable) {
      products.push(this.websiteTenWebTheme);
    }
    if (!this.websiteTenWebTheme || (this.websiteTenWebTheme && !this.websiteTenWebTheme.active)) {
      this.installAndActivate(this.websiteTenWebTheme);
      if (!products.length) {
        return true;
      }
    }
    if (products.length) {
      this._store.dispatch(new fromStore.InitWebsiteProductAction({
        website: this.website,
        products: products,
        action: ActivityAction.UPDATE,
        network: 1
      }));
      this._availableUpdatesCount = products.length;
      return true;
    }

    return false;
  }

  public importTenWebTemplate(template: BuilderTemplates): void {
    this._activeModal.hideCloseBtn(true);
    this._activeModal.setBackdrop('static');
    this._activeModal.resize(432, 471);
    this.step = 'recreateBuilder';
    this.recreationStep = 'recreating';
    const data = {
      template: template
    };
    this._doRequiredActionsBeforeRun() ? this._getPluginActivity(data, 'import-template') : this._importTemplate(template);
  }

  private _importTemplate(template): void {
    this.importTemplateType = 'pre-made-page';
    this.templateInfo = this.templateCategory + " - " + template.title;
    this._getOngoingActivity();
    this._store.dispatch(new fromStore.LoadImportingBuilderTemplate({
      website: this.website,
      templateId: template.id,
      templateType: template.type,
      templateTitle: template.title,
      kitData: !TypeUtils.isNull(this.kitData) ? this.kitData : null,
      menuItemPosition: this.menuItemPosition,
      menuItemId: this.menuItemId,
      menuTermId: this.menuTermId,
      postStatus: (this.trialUser || !this.hasFrontPage) ? 'publish' : 'draft',
    }));
  }

  public addTemplateCategory(templateCategory: string) {
    this.templateCategory = templateCategory;
  }

  public showUpgradePopup(value) {
    this._activeModal.close({showUpgrade: 'openUpgrade', from: value});
  }

  private _recreateWebsite(url, pageTitle?, kitData?) {
    const values = [url];
    this._getOngoingActivity();
    this.recreateStarted = true;
    this._websiteService.runAiBuilder(this.website.id, values, pageTitle ? pageTitle : 'Title', (this.trialUser || !this.hasFrontPage) ? 'publish' : 'draft', kitData, !(this.pages && this.pages.length), this.menuItemPosition, this.menuItemId, this.menuTermId)
      .pipe(
        first((response: DbResponse): boolean => !!response)
      )
      .subscribe((response: DbResponse): void => {
        this._interrupt$.next(true);
        if (!response.success) {
          // this.errorDialog(this.website);
        } else {
          this.importTemplateType = 'create-page-ai';
        }
      });
  }

  public addPage(title: string): void {
    this._websiteService.createBlankPage(this.website.id, title, this.trialUser ? 'publish' : 'draft', this.menuItemPosition, this.menuItemId, this.menuTermId)
      .pipe(
        first(response => !!response)
      )
      .subscribe(response => {
        this._activeModal.close();
        this._modalService.dismissAll();
        if (response.success) {
          this.showSuccessDialog('blank_page', response.data && response.data.page_url ? response.data.page_url : null, title);
        }
      });
  }

  private _getPageRedirectUrl = (url: string): string => {
    const postId: string = (new URL(url)).searchParams.get('post');
    return `${this.website.site_url}?p=${postId}`;
  };
}
